import { useContext, useEffect, useState } from "react";
import BarMenu from "../BarMenu";
import api from "../../utils/api";
import "./styles.scss";
import { Context } from "../../context/Contex";

export default function Export() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [copied, setCopied] = useState(false);

    const { refreshUnreadCount, stats } = useContext(Context);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const response = await api.get('/analytics');
            if (response.data.success) {
                setAnalytics(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao buscar analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);

            const response = await api.get('/export/xlsx', {
                responseType: 'blob'
            });

            // Criar URL do blob e fazer download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `exportacao_bipagem_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            refreshUnreadCount()

        } catch (error) {
            console.error('Erro ao exportar:', error);
            alert('Erro ao exportar dados. Tente novamente.');
        } finally {
            setExporting(false);
        }
    };

    const handleCopyReport = async () => {
        if (!analytics || !stats) return;

        // DATA NORMAL
        const hoje = new Date();

        const dia = String(hoje.getDate()).padStart(2, '0');
        const mes = String(hoje.getMonth() + 1).padStart(2, '0');
        const ano = hoje.getFullYear();

        // ─────────────────────────────────────────────
        // DATA AJUSTADA
        // ENTRE 00:00 E 05:59 VOLTA 1 DIA
        // ─────────────────────────────────────────────
        const adjustedDate = new Date();

        const currentHour = adjustedDate.getHours();

        if (currentHour >= 0 && currentHour < 6) {
            adjustedDate.setDate(adjustedDate.getDate() - 1);
        }

        const adjustedDay = String(adjustedDate.getDate()).padStart(2, '0');
        const adjustedMonth = String(adjustedDate.getMonth() + 1).padStart(2, '0');
        const adjustedYear = adjustedDate.getFullYear();

        const sumStockBipados = (stockBipados) => {
            if (!stockBipados) return 0;
            return Object.values(stockBipados).reduce((acc, val) => acc + (val || 0), 0);
        };

        const entrega = analytics.porAssunto?.Entrega;
        const devolucao = analytics.porAssunto?.Devolucao;
        const redespacho = analytics.porAssunto?.Redespacho;

        const entregaTickets = sumStockBipados(entrega?.stockBipados);
        const redespachoTickets = sumStockBipados(redespacho?.stockBipados);
        const devolucaoCards = sumStockBipados(devolucao?.stockBipados);

        const loc = analytics.entregaByRouteType?.LOC ?? 0;
        const met = analytics.entregaByRouteType?.MET ?? 0;
        const int_ = analytics.entregaByRouteType?.INT ?? 0;

        const mensagem =
            `*🔷 PASSAGEM DE TURNO – CARDS (NC)*
📅 ${dia}/${mes}/${ano}

*📊 Panorama Geral*
• Cards verificados: *${stats.uniqueCount}*
• Cards localizados: *${stats.found}*

*📦 Fluxos Operacionais*
• Retornos da Expedição: Cards conferidos e organizados.
• NC (D01–D45): Checagem 100%
• Redespacho: Cards identificados e separados
• Retornos do Correios: Separado e identificado

*📁 Tratativas NC*
• Entrega (Voltar para rua): *${entregaTickets}* Tickets localizados
     ├─ LOC: *${loc}* Cards (Todos em caf)
     ├─ Met: *${met}* Cards (Todos em caf)
     └─ INT: *${int_}* Cards (Todos em caf)
• Redespacho: *${redespachoTickets}* Tickets localizados
• Devolução: *${devolucaoCards}* Cards identificados e embarcados

*🖥️ Base de Dados*
• Power BI às *19:46 - ${adjustedDay}/${adjustedMonth}/${adjustedYear}*
• ICS às *${analytics.message.dateICS}*
• Volume total: *${analytics.geral.totalConjuntos}* tickets

*🔄 Status de Retornos*
• Triagem: concluída (100%)
• CAF: concluído (100%)

*📝 Nota do Turno*
\`Sem intercorrências ou pendências registradas.\`

—
Atte.
*Alan H. Silva*`;

        try {
            await navigator.clipboard.writeText(mensagem);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        } catch (err) {
            console.error('Erro ao copiar:', err);
        }
    };

    if (loading) {
        return (
            <div id="Export">
                <BarMenu />
                <section className="export-section">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Carregando dados...</p>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div id="Export">
            <BarMenu />
            <section className="export-section">
                <header className="export-header">
                    <div>
                        <h1>📥 Exportar Dados</h1>
                        <p className="subtitle">Baixe os dados de bipagem em formato Excel</p>
                    </div>
                </header>

                {/* Cards de Resumo */}
                {analytics && (
                    <div className="summary-cards">
                        <div className="summary-card total">
                            <div className="card-icon">📦</div>
                            <div className="card-info">
                                <span className="label">Total de Itens</span>
                                <span className="value">{analytics.geral.totalConjuntos}</span>
                            </div>
                        </div>
                        <div className="summary-card success">
                            <div className="card-icon">✓</div>
                            <div className="card-info">
                                <span className="label">Bipados</span>
                                <span className="value">{analytics.geral.totalBipados}</span>
                            </div>
                        </div>
                        <div className="summary-card warning">
                            <div className="card-icon">⏳</div>
                            <div className="card-info">
                                <span className="label">Não Bipados</span>
                                <span className="value">{analytics.geral.totalNaoBipados}</span>
                            </div>
                        </div>
                        <div className="summary-card percentage">
                            <div className="card-icon">📈</div>
                            <div className="card-info">
                                <span className="label">Progresso</span>
                                <span className="value">{analytics.geral.percentualBipado}%</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Área de Exportação */}
                <div className="export-area">
                    <div className="export-card">
                        <div className="export-icon">📊</div>
                        <h2>Exportar para Excel</h2>
                        <p>O arquivo incluirá:</p>
                        <ul className="export-includes">
                            <li>✓ Todos os AWBs do catálogo</li>
                            <li>✓ Status de bipagem (Sim/Não)</li>
                            <li>✓ Assunto, Embarcador e Rota</li>
                            <li>✓ Data/hora da última bipagem</li>
                            <li>✓ Localização do último registro</li>
                            <li>✓ Nome de quem exportou</li>
                            <li>✓ Aba com resumo estatístico</li>
                        </ul>
                        <button
                            className={`export-btn ${exporting ? 'exporting' : ''}`}
                            onClick={handleExport}
                            disabled={exporting}
                        >
                            {exporting ? (
                                <>
                                    <div className="btn-spinner"></div>
                                    Gerando arquivo...
                                </>
                            ) : (
                                <>
                                    📥 Baixar XLSX
                                </>
                            )}
                        </button>
                        <button
                            className={`copy-report-btn ${copied ? 'copied' : ''}`}
                            onClick={handleCopyReport}
                            disabled={!analytics || !stats}
                        >
                            {copied ? (
                                <>✅ Mensagem copiada!</>
                            ) : (
                                <>📋 Copiar Relatório de Turno</>
                            )}
                        </button>
                    </div>

                    {/* Preview dos dados */}
                    {analytics && (
                        <div className="preview-card">
                            <h3>📋 Prévia dos Dados</h3>
                            <div className="preview-sections">
                                <div className="preview-section">
                                    <h4>Por Assunto (Top 5)</h4>
                                    <div className="preview-list">
                                        {Object.entries(analytics.porAssunto)
                                            .sort((a, b) => b[1].total - a[1].total)
                                            .slice(0, 5)
                                            .map(([assunto, stats]) => (
                                                <div key={assunto} className="preview-item">
                                                    <span className="name">{assunto}</span>
                                                    <span className="stats">
                                                        {stats.bipados}/{stats.total}
                                                    </span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                                <div className="preview-section">
                                    <h4>Por Embarcador (Top 5)</h4>
                                    <div className="preview-list">
                                        {Object.entries(analytics.porEmbarcador)
                                            .sort((a, b) => b[1].total - a[1].total)
                                            .slice(0, 5)
                                            .map(([emb, stats]) => (
                                                <div key={emb} className="preview-item">
                                                    <span className="name">{emb}</span>
                                                    <span className="stats">
                                                        {stats.bipados}/{stats.total}
                                                    </span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}